import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { EMPRESAS, EMPRESA_META, type Empresa } from "@brasilnovo/shared";
import { api } from "../lib/api.js";
import { Card, ErrorBox, Spinner, num } from "../components/ui.js";
import { fmtColetaDia, marcadores, situacaoHorario } from "../lib/carga.js";
import {
  ESTADO_META, agoraSP, diaInicial, montarPainel, resumirPainel, type LinhaPainel,
} from "../lib/painel.js";

// Painel de carregamentos — pedido da torre (30/10/2026): "um painel para
// todos estarem acompanhando quem e quando estará em carregamento". Só
// leitura, de propósito: editar e confirmar continuam na Torre de Controle.
const REFRESH_MS = 30_000;
const MESES = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
const rotuloCompetencia = (c: string) => `${MESES[Number(c.slice(5, 7)) - 1]}/${c.slice(0, 4)}`;
const DIAS = ["domingo", "segunda", "terça", "quarta", "quinta", "sexta", "sábado"];
const rotuloDia = (d: string) =>
  `${DIAS[new Date(Date.UTC(+d.slice(0, 4), +d.slice(5, 7) - 1, +d.slice(8, 10))).getUTCDay()]}, ${d.slice(8, 10)}/${d.slice(5, 7)}`;

interface Resumo { competencia: string | null; competencias: string[] }

function Relogio({ agora }: { agora: string }) {
  return (
    <div className="text-right" data-testid="painel-relogio">
      <p className="text-3xl font-bold tabular-nums text-slate-900">{agora.slice(11, 16)}</p>
      <p className="text-xs text-slate-500">{rotuloDia(agora.slice(0, 10))}</p>
    </div>
  );
}

function Kpi({ label, valor, cls }: { label: string; valor: number; cls: string }) {
  return (
    <div className={`rounded-xl px-4 py-3 ${cls}`}>
      <p className="text-[11px] font-semibold uppercase tracking-wide opacity-80">{label}</p>
      <p className="text-2xl font-bold tabular-nums">{num(valor)}</p>
    </div>
  );
}

export function Painel() {
  const [agora, setAgora] = useState(() => agoraSP());
  useEffect(() => { const t = setInterval(() => setAgora(agoraSP()), 20_000); return () => clearInterval(t); }, []);
  const [competencia, setCompetencia] = useState<string | null>(null);
  const [empresa, setEmpresa] = useState<Empresa | "">("");
  const [dia, setDia] = useState<string | "todos" | null>(null);

  const resumo = useQuery({
    queryKey: ["painel-resumo"],
    queryFn: async () => (await api.get("/operacoes/torre")).data as Resumo,
  });
  const comp = competencia ?? resumo.data?.competencia ?? null;

  const cargas = useQuery({
    queryKey: ["painel-cargas", comp, empresa],
    enabled: !!comp,
    refetchInterval: REFRESH_MS,
    placeholderData: (prev) => prev,
    queryFn: async () => {
      const qs = new URLSearchParams({ fase: "AGENDADA", limit: "500", ano: comp!.slice(0, 4), mes: String(Number(comp!.slice(5, 7))) });
      if (empresa) qs.set("empresa", empresa);
      return (await api.get(`/operacoes?${qs}`)).data as LinhaPainel[];
    },
  });

  const dias = useMemo(() => montarPainel(cargas.data ?? [], agora), [cargas.data, agora]);
  const kpis = useMemo(() => resumirPainel(dias, agora), [dias, agora]);
  const diaAtivo = dia ?? diaInicial(dias, agora) ?? "todos";
  const visiveis = diaAtivo === "todos" ? dias : dias.filter((d) => d.dia === diaAtivo || d.dia == null);

  const telaCheia = () => {
    const el = document.documentElement;
    if (document.fullscreenElement) void document.exitFullscreen();
    else void el.requestFullscreen?.();
  };

  return (
    <div data-testid="painel">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Painel de carregamentos</h1>
          <p className="mt-1 max-w-2xl text-sm text-slate-500">
            Quem e quando estará em carregamento, em sequência de coleta. Tela só de acompanhamento: atualiza sozinha a cada 30 s.
            Para vincular, confirmar horário ou editar, use a <Link to="/torre" className="font-medium text-brand-700 hover:underline">Torre de Controle</Link>.
          </p>
        </div>
        <Relogio agora={agora} />
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <select className="input w-36" data-testid="painel-competencia" value={comp ?? ""} onChange={(e) => { setCompetencia(e.target.value); setDia(null); }}>
          {(resumo.data?.competencias ?? []).map((c) => <option key={c} value={c}>{rotuloCompetencia(c)}</option>)}
        </select>
        <select className="input w-44" data-testid="painel-empresa" value={empresa} onChange={(e) => { setEmpresa(e.target.value as Empresa | ""); setDia(null); }}>
          <option value="">Todas as empresas</option>
          {EMPRESAS.map((e) => <option key={e} value={e}>{EMPRESA_META[e].label}</option>)}
        </select>
        <button type="button" className="ml-auto rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50" onClick={telaCheia}>
          ⛶ Tela cheia
        </button>
        {cargas.isFetching && <span className="text-xs text-brand-600">atualizando…</span>}
      </div>

      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-5" data-testid="painel-kpis">
        <Kpi label="Agendadas" valor={kpis.total} cls="bg-white text-slate-900 ring-1 ring-slate-200" />
        <Kpi label="Hoje" valor={kpis.hoje} cls="bg-sky-50 text-sky-900 ring-1 ring-sky-200" />
        <Kpi label="Carregando agora" valor={kpis.agora} cls="bg-emerald-50 text-emerald-900 ring-1 ring-emerald-200" />
        <Kpi label="Horário passou" valor={kpis.atrasadas} cls="bg-red-50 text-red-900 ring-1 ring-red-200" />
        <Kpi label="Aguardando a torre" valor={kpis.aguardando} cls="bg-amber-50 text-amber-900 ring-1 ring-amber-200" />
      </div>

      {dias.some((d) => d.dia) && (
        <div className="mb-4 flex flex-wrap gap-1.5" data-testid="painel-dias">
          <button type="button" onClick={() => setDia("todos")}
            className={`rounded-full px-3 py-1 text-xs font-semibold ring-1 ${diaAtivo === "todos" ? "bg-brand-600 text-white ring-brand-600" : "bg-white text-slate-600 ring-slate-200 hover:bg-slate-50"}`}>
            Todos os dias
          </button>
          {dias.filter((d) => d.dia).map((d) => (
            <button key={d.dia} type="button" onClick={() => setDia(d.dia)} data-dia={d.dia}
              className={`rounded-full px-3 py-1 text-xs font-semibold ring-1 ${diaAtivo === d.dia ? "bg-brand-600 text-white ring-brand-600" : d.dia === agora.slice(0, 10) ? "bg-sky-50 text-sky-800 ring-sky-300" : "bg-white text-slate-600 ring-slate-200 hover:bg-slate-50"}`}>
              {fmtColetaDia(d.dia!).trim()} · {d.linhas.length}
            </button>
          ))}
        </div>
      )}

      {cargas.isError ? <ErrorBox error={cargas.error} />
        : cargas.isLoading || resumo.isLoading ? <Spinner />
        : visiveis.length === 0 ? <Card><p className="text-sm text-slate-500">Nenhuma carga agendada neste filtro.</p></Card>
        : (
          <div className="space-y-5">
            {visiveis.map((d) => (
              <section key={d.dia ?? "sem"} data-testid="painel-dia" data-dia={d.dia ?? ""}>
                <h2 className={`mb-2 text-sm font-bold uppercase tracking-wide ${d.dia === agora.slice(0, 10) ? "text-sky-700" : "text-slate-500"}`}>
                  {d.dia ? <>{rotuloDia(d.dia)}{d.dia === agora.slice(0, 10) && " · hoje"}</> : "Sem horário confirmado"}
                  <span className="ml-2 font-normal normal-case text-slate-400">{d.linhas.length} carga(s)</span>
                </h2>
                <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
                  <ul className="divide-y divide-slate-100">
                    {d.linhas.map((l) => {
                      const pedido = situacaoHorario(l);
                      return (
                        <li key={l.id} data-testid="painel-linha" data-estado={l.estado} data-ordem={l.numero_ordem}
                          className={`grid grid-cols-[72px_minmax(0,1fr)] items-center gap-x-4 gap-y-1 px-4 py-3 sm:grid-cols-[84px_minmax(0,1.3fr)_minmax(0,1.3fr)_minmax(0,0.9fr)_230px] ${l.estado === "agora" ? "bg-emerald-50/60" : l.estado === "atrasada" ? "bg-red-50/40" : ""}`}>
                          <div className="row-span-2 sm:row-span-1">
                            <p className="text-2xl font-bold tabular-nums text-slate-900">{l.data_hora_coleta ? fmtColetaDia(l.data_hora_coleta).slice(-5) : "--:--"}</p>
                            {pedido === "aguardando" && <p className="text-[11px] italic text-amber-700">pedido {fmtColetaDia(l.data_hora_solicitada).slice(4)}</p>}
                          </div>
                          <div className="min-w-0">
                            <p className="truncate font-semibold text-slate-900" title={l.motorista ?? ""}>{l.motorista || "a vincular"}</p>
                            <p className="truncate text-xs text-slate-500">
                              {l.cavalo ? <>cavalo <b className="text-slate-700">{l.cavalo}</b> · </> : null}
                              {EMPRESA_META[l.empresa as Empresa]?.label ?? l.empresa} · ordem {l.numero_ordem}
                            </p>
                          </div>
                          <div className="min-w-0">
                            <p className="truncate font-semibold text-slate-800">📍 {l.cidade_entrega || "cidade não informada"}</p>
                            <p className="truncate text-xs text-slate-500" title={l.cliente_entrega ?? ""}>{l.cliente_entrega || "—"}</p>
                          </div>
                          <div className="hidden min-w-0 text-xs text-slate-500 sm:block">
                            {l.patio_coleta && <p>pátio <b className="text-slate-700">{l.patio_coleta}</b></p>}
                            <p>{l.tipo_veiculo && <>veículo <b className="text-slate-700">{l.tipo_veiculo}</b></>}{l.peso != null && <> · {num(l.peso)} t</>}</p>
                          </div>
                          <div className="col-span-2 flex flex-wrap items-center gap-1 sm:col-span-1 sm:justify-end">
                            {marcadores(l).map((m) => <span key={m.chave} className={`rounded-md px-1.5 py-0.5 text-[11px] font-semibold ${m.cls}`}>{m.label}</span>)}
                            <span className={`whitespace-nowrap rounded-md px-2 py-0.5 text-[11px] font-semibold ${ESTADO_META[l.estado].cls}`} data-testid="painel-estado">
                              {ESTADO_META[l.estado].label}
                            </span>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </section>
            ))}
          </div>
        )}
    </div>
  );
}
